/*
 * Copyright (c) 2026. Orion Analytics. All Rights Reserved
 */

import React, { useState, useMemo } from "react";

const WealthCalculator = () => {
  const [monthlySIP, setMonthlySIP] = useState(25000);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [years, setYears] = useState(15);

  // SIP Future Value Calculation
  const calculation = useMemo(() => {
    const monthlyRate = expectedReturn / 100 / 12;
    const totalMonths = years * 12;

    // Future Value of SIP Formula
    const futureValue =
      monthlySIP *
      (((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate) *
        (1 + monthlyRate));

    const investedAmount = monthlySIP * totalMonths;
    const gain = futureValue - investedAmount;

    return {
      futureValue,
      investedAmount,
      gain,
    };
  }, [monthlySIP, expectedReturn, years]);

  // Currency Formatter
  const formatCurrency = (num) => {
    return new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(num);
  };

  return (
    <div className="max-w-xl mx-auto bg-[#f3f4f5] p-10 rounded-[32px] shadow-sm">

      <h2 className="text-5xl font-bold mb-10 text-[#111]">
        Wealth Calculator
      </h2>

      {/* MONTHLY SIP */}
      <div className="mb-10">
        <div className="flex justify-between mb-1">
          <label className="font-semibold text-lg">
            Monthly SIP Amount
          </label>

          <span className="text-green-700 font-bold text-xl">
            ₹{formatCurrency(monthlySIP)}
          </span>
        </div>

        <input
          type="range"
          min="1000"
          max="100000"
          step="500"
          value={monthlySIP}
          onChange={(e) => setMonthlySIP(Number(e.target.value))}
          className="w-full accent-green-700"
        />
      </div>

      {/* EXPECTED RETURN */}
      <div className="mb-10">
        <div className="flex justify-between mb-1">
          <label className="font-semibold text-lg">
            Expected Return (%)
          </label>

          <span className="text-green-700 font-bold text-xl">
            {expectedReturn}%
          </span>
        </div>

        <input
          type="range"
          min="1"
          max="30"
          step="0.5"
          value={expectedReturn}
          onChange={(e) => setExpectedReturn(Number(e.target.value))}
          className="w-full accent-green-700"
        />
      </div>

      {/* TIME PERIOD */}
      <div className="mb-10">
        <div className="flex justify-between mb-1">
          <label className="font-semibold text-lg">
            Time Period (Years)
          </label>

          <span className="text-green-700 font-bold text-xl">
            {years} Years
          </span>
        </div>

        <input
          type="range"
          min="1"
          max="40"
          step="1"
          value={years}
          onChange={(e) => setYears(Number(e.target.value))}
          className="w-full accent-green-700"
        />
      </div>

      {/* RESULT CARD */}
      <div className="bg-green-500 text-white p-8 rounded-[28px]">

        <p className="text-lg opacity-90 mb-3">
          Estimated Wealth After {years} Years
        </p>

        <h3 className="text-6xl font-extrabold mb-8">
          ₹{formatCurrency(calculation.futureValue)}
        </h3>

        <div className="border-t border-green-500 pt-8 flex justify-between text-lg">

          <div>
            <p className="opacity-80 mb-1">Invested</p>
            <p className="font-bold">
              ₹{formatCurrency(calculation.investedAmount)}
            </p>
          </div>

          <div className="text-right">
            <p className="opacity-80 mb-1">Gain</p>
            <p className="font-bold">
              ₹{formatCurrency(calculation.gain)}
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default WealthCalculator;